import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';

// Date preset options
const DATE_PRESETS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

// Helper to get date range from preset
const getDateRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (preset) {
    case 'this_week': {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return { from: startOfWeek, to: today };
    }
    case 'this_month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: startOfMonth, to: today };
    }
    case 'last_month': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: startOfLastMonth, to: endOfLastMonth };
    }
    case 'last_3_months': {
      const start = new Date(today.getFullYear(), today.getMonth() - 3, 1);
      return { from: start, to: today };
    }
    case 'this_year': {
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      return { from: startOfYear, to: today };
    }
    default:
      return { from: today, to: today };
  }
};

const formatDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatCurrency = (amount) => {
  return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function FinancialReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('collection');
  const [loading, setLoading] = useState(false);
  
  // Date filters
  const [datePreset, setDatePreset] = useState('this_month');
  const [fromDate, setFromDate] = useState(formatDate(getDateRange('this_month').from));
  const [toDate, setToDate] = useState(formatDate(getDateRange('this_month').to));
  
  // Report data
  const [collectionData, setCollectionData] = useState(null);
  const [gstData, setGstData] = useState(null);
  const [refundData, setRefundData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [universityData, setUniversityData] = useState(null);

  // Handle preset change
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const range = getDateRange(preset);
      setFromDate(formatDate(range.from));
      setToDate(formatDate(range.to));
    }
  };

  // Fetch Collection Report
  const fetchCollectionReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/finance/reports/collection?from_date=${fromDate}&to_date=${toDate}`);
      setCollectionData(response.data);
    } catch (error) {
      toast.error('Failed to fetch collection report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch GST Report
  const fetchGSTReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/finance/reports/gst?from_date=${fromDate}&to_date=${toDate}`);
      setGstData(response.data);
    } catch (error) {
      toast.error('Failed to fetch GST report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch Refund Report
  const fetchRefundReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/finance/reports/refund?from_date=${fromDate}&to_date=${toDate}`);
      setRefundData(response.data);
    } catch (error) {
      toast.error('Failed to fetch refund report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch Counsellor Revenue Report
  const fetchRevenueReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/finance/reports/counsellor-revenue?from_date=${fromDate}&to_date=${toDate}`);
      setRevenueData(response.data);
    } catch (error) {
      toast.error('Failed to fetch revenue report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch University Report
  const fetchUniversityReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/finance/reports/university-payable?from_date=${fromDate}&to_date=${toDate}`);
      setUniversityData(response.data);
    } catch (error) {
      toast.error('Failed to fetch university report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch report based on active tab
  const fetchReport = () => {
    switch (activeTab) {
      case 'collection': fetchCollectionReport(); break;
      case 'gst': fetchGSTReport(); break;
      case 'refund': fetchRefundReport(); break;
      case 'revenue': fetchRevenueReport(); break;
      case 'university': fetchUniversityReport(); break;
      default: break;
    }
  };

  // Export to CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${fromDate}_to_${toDate}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <Button variant="ghost" onClick={() => navigate('/finance')} className="self-start h-8 text-xs sm:text-sm px-2">
          <i className="fas fa-arrow-left mr-1 sm:mr-2"></i> Back to Finance
        </Button>
        <div>
          <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900">
            <i className="fas fa-chart-bar mr-2 sm:mr-3 text-indigo-600"></i>
            Financial Reports
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Generate and export financial reports with GST breakdown</p>
        </div>
      </div>

      {/* Date Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="space-y-3">
            {/* Date Range Selector */}
            <div>
              <Label className="text-xs sm:text-sm">Date Range</Label>
              <Select value={datePreset} onValueChange={handlePresetChange}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Inputs - Row on mobile */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div>
                <Label className="text-xs sm:text-sm">From Date</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); }}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">To Date</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); }}
                  className="h-9 text-sm"
                />
              </div>
            </div>
            
            {/* Generate Button - Full width on mobile */}
            <Button onClick={fetchReport} disabled={loading} className="w-full h-9 text-sm" data-testid="financial-report-generate">
              {loading ? <i aria-hidden="true" className="fas fa-spinner fa-spin mr-2"></i> : <i aria-hidden="true" className="fas fa-search mr-2"></i>}
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto p-1 gap-1">
          <TabsTrigger value="collection" data-testid="tab-collection" className="flex-1 min-w-[60px] text-xs sm:text-sm px-2 py-1.5">
            <i className="fas fa-coins mr-1 hidden sm:inline"></i> Collection
          </TabsTrigger>
          <TabsTrigger value="gst" data-testid="tab-gst" className="flex-1 min-w-[40px] text-xs sm:text-sm px-2 py-1.5">
            <i className="fas fa-percentage mr-1 hidden sm:inline"></i> GST
          </TabsTrigger>
          <TabsTrigger value="refund" data-testid="tab-refund" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5">
            <i className="fas fa-undo mr-1 hidden sm:inline"></i> Refunds
          </TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5">
            <i className="fas fa-users mr-1 hidden sm:inline"></i> Revenue
          </TabsTrigger>
          <TabsTrigger value="university" data-testid="tab-university" className="flex-1 min-w-[50px] text-xs sm:text-sm px-2 py-1.5">
            <i className="fas fa-university mr-1 hidden sm:inline"></i> Univ
          </TabsTrigger>
        </TabsList>

        {/* Collection Report */}
        <TabsContent value="collection" className="mt-4">
          {collectionData ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-green-700 truncate">{formatCurrency(collectionData.collection.net_amount)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Gross Collection</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-blue-700 truncate">{formatCurrency(collectionData.collection.base_amount)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Base Amount</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-purple-700 truncate">{formatCurrency(collectionData.collection.total_gst)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">GST Collected</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-red-700 truncate">{formatCurrency(collectionData.refunds.amount)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Refunds</p>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Table */}
              <Card>
                <CardHeader className="p-3 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                  <CardTitle className="text-base sm:text-lg">Collection Summary</CardTitle>
                  <Badge variant="outline" className="text-[10px] sm:text-xs self-start sm:self-auto">{collectionData.period.from} to {collectionData.period.to}</Badge>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>Gross Amount</TableCell>
                        <TableCell className="text-right">{formatCurrency(collectionData.collection.gross_amount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Base Amount (Excl. GST)</TableCell>
                        <TableCell className="text-right">{formatCurrency(collectionData.collection.base_amount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>CGST (9%)</TableCell>
                        <TableCell className="text-right">{formatCurrency(collectionData.collection.cgst)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>SGST (9%)</TableCell>
                        <TableCell className="text-right">{formatCurrency(collectionData.collection.sgst)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total GST</TableCell>
                        <TableCell className="text-right">{formatCurrency(collectionData.collection.total_gst)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Payments</TableCell>
                        <TableCell className="text-right">{collectionData.collection.total_payments}</TableCell>
                      </TableRow>
                      <TableRow className="bg-red-50">
                        <TableCell className="font-medium text-red-700">Less: Refunds</TableCell>
                        <TableCell className="text-right text-red-700">({formatCurrency(collectionData.refunds.amount)})</TableCell>
                      </TableRow>
                      <TableRow className="bg-green-50 font-bold">
                        <TableCell className="text-green-700">Net Revenue</TableCell>
                        <TableCell className="text-right text-green-700">{formatCurrency(collectionData.net_revenue.amount)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <i className="fas fa-chart-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Click "Generate Report" to view collection data</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* GST Report */}
        <TabsContent value="gst" className="mt-4">
          {gstData ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <Card className="bg-gradient-to-br from-indigo-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-indigo-700 truncate">{formatCurrency(gstData.summary.total_taxable_value)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Taxable Value</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-blue-700 truncate">{formatCurrency(gstData.summary.total_cgst)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">CGST (9%)</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-purple-700 truncate">{formatCurrency(gstData.summary.total_sgst)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">SGST (9%)</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-green-700 truncate">{formatCurrency(gstData.summary.total_gst_payable)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Total GST Payable</p>
                  </CardContent>
                </Card>
              </div>

              {/* Period-wise breakdown */}
              <Card>
                <CardHeader className="p-3 sm:p-6 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                  <CardTitle className="text-base sm:text-lg">GST Summary for Tax Filing</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => exportToCSV(gstData.period_wise_collection, 'gst_report')} className="self-start sm:self-auto h-8 text-xs sm:text-sm">
                    <i className="fas fa-download mr-2"></i> Export CSV
                  </Button>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 overflow-x-auto">
                  {gstData.period_wise_collection.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Period</TableHead>
                          <TableHead className="text-right">Taxable Value</TableHead>
                          <TableHead className="text-right">CGST</TableHead>
                          <TableHead className="text-right">SGST</TableHead>
                          <TableHead className="text-right">Total GST</TableHead>
                          <TableHead className="text-right">Invoices</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gstData.period_wise_collection.map(row => (
                          <TableRow key={row._id ?? 'unknown-period'} data-testid={`gst-period-${row._id ?? 'unknown'}`}>
                            <TableCell>{row._id || 'N/A'}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.taxable_value)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.cgst)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.sgst)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.total_gst)}</TableCell>
                            <TableCell className="text-right">{row.invoice_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No GST data for this period</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <i className="fas fa-percentage text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Click "Generate Report" to view GST data</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Refund Report */}
        <TabsContent value="refund" className="mt-4">
          {refundData ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <Card className="bg-gradient-to-br from-red-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-red-700">{refundData.summary.total_refunds}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Total Refunds</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-orange-700 truncate">{formatCurrency(refundData.summary.total_amount)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Refund Amount</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-purple-700 truncate">{formatCurrency(refundData.summary.cgst_reversed)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">CGST Reversed</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-indigo-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-indigo-700 truncate">{formatCurrency(refundData.summary.sgst_reversed)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">SGST Reversed</p>
                  </CardContent>
                </Card>
              </div>

              {/* By Reason */}
              {refundData.by_reason.length > 0 && (
                <Card>
                  <CardHeader className="p-3 sm:p-6">
                    <CardTitle className="text-base sm:text-lg">Refunds by Reason</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">CGST</TableHead>
                          <TableHead className="text-right">SGST</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {refundData.by_reason.map(row => (
                          <TableRow key={row._id ?? 'unspecified-reason'} data-testid={`refund-reason-${row._id ?? 'unspecified'}`}>
                            <TableCell>{row._id || 'Not specified'}</TableCell>
                            <TableCell className="text-right">{row.count}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.total_amount)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.cgst_reversed)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.sgst_reversed)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Detailed List */}
              <Card>
                <CardHeader className="p-3 sm:p-6 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                  <CardTitle className="text-base sm:text-lg">Refund Details</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => exportToCSV(refundData.refunds, 'refund_report')} className="self-start sm:self-auto h-8 text-xs sm:text-sm">
                    <i className="fas fa-download mr-2"></i> Export CSV
                  </Button>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
                  {refundData.refunds.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>Student</TableHead>
                            <TableHead>Refund Date</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {refundData.refunds.slice(0, 20).map((refund) => (
                            <TableRow key={refund.id}>
                              <TableCell>
                                <Badge variant="outline">{refund.invoice_number || 'N/A'}</Badge>
                              </TableCell>
                              <TableCell>{refund.student_name}</TableCell>
                              <TableCell>{refund.refund_date ? new Date(refund.refund_date).toLocaleDateString() : '-'}</TableCell>
                              <TableCell>{refund.refund_reason || '-'}</TableCell>
                              <TableCell className="text-right font-medium text-red-600">{formatCurrency(refund.refund_amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {refundData.refunds.length > 20 && (
                        <p className="text-center text-sm text-gray-500 mt-4">Showing 20 of {refundData.refunds.length} refunds. Export CSV for full list.</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No refunds in this period</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <i className="fas fa-undo text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Click "Generate Report" to view refund data</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Counsellor Revenue Report */}
        <TabsContent value="revenue" className="mt-4">
          {revenueData ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-green-700 truncate">{formatCurrency(revenueData.grand_total.net_revenue)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Net Revenue</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-blue-700">{revenueData.grand_total.total_invoices}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Total Invoices</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-purple-700 truncate">{formatCurrency(revenueData.grand_total.gst_collected)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">GST Collected</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-red-700 truncate">{formatCurrency(revenueData.grand_total.refund_amount)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Refunds</p>
                  </CardContent>
                </Card>
              </div>

              {/* By Counsellor */}
              <Card>
                <CardHeader className="p-3 sm:p-6 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                  <CardTitle className="text-base sm:text-lg">Revenue by Counsellor</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => exportToCSV(revenueData.by_counsellor, 'counsellor_revenue')} className="self-start sm:self-auto h-8 text-xs sm:text-sm">
                    <i className="fas fa-download mr-2"></i> Export CSV
                  </Button>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 overflow-x-auto">
                  {revenueData.by_counsellor.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Counsellor</TableHead>
                          <TableHead className="text-right">Invoices</TableHead>
                          <TableHead className="text-right">Gross Collection</TableHead>
                          <TableHead className="text-right">GST</TableHead>
                          <TableHead className="text-right">Refunds</TableHead>
                          <TableHead className="text-right">Net Revenue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {revenueData.by_counsellor.map(row => (
                          <TableRow key={row.counsellor_id ?? 'unassigned-counsellor'} data-testid={`revenue-counsellor-${row.counsellor_id ?? 'unassigned'}`}>
                            <TableCell className="font-medium">{row.counsellor_name || 'Unassigned'}</TableCell>
                            <TableCell className="text-right">{row.total_invoices}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.gross_collection)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.gst_collected)}</TableCell>
                            <TableCell className="text-right text-red-600">{formatCurrency(row.refund_amount)}</TableCell>
                            <TableCell className="text-right font-bold text-green-600">{formatCurrency(row.net_revenue)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-gray-50 font-bold">
                          <TableCell>TOTAL</TableCell>
                          <TableCell className="text-right">{revenueData.grand_total.total_invoices}</TableCell>
                          <TableCell className="text-right">{formatCurrency(revenueData.grand_total.gross_collection)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(revenueData.grand_total.gst_collected)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(revenueData.grand_total.refund_amount)}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(revenueData.grand_total.net_revenue)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No revenue data for this period</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <i className="fas fa-user-tie text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Click "Generate Report" to view counsellor revenue</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* University Report */}
        <TabsContent value="university" className="mt-4">
          {universityData ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <Card className="bg-gradient-to-br from-indigo-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-indigo-700">{universityData.summary.total_universities}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Universities</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-blue-700">{universityData.summary.total_students}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Students</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-lg sm:text-2xl font-bold text-green-700 truncate">{formatCurrency(universityData.summary.total_collected)}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">Total Collected</p>
                  </CardContent>
                </Card>
              </div>

              {/* By University */}
              <Card>
                <CardHeader className="p-3 sm:p-6 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="text-base sm:text-lg">Collection by University</CardTitle>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">{universityData.note}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => exportToCSV(universityData.by_university, 'university_report')} className="self-start sm:self-auto h-8 text-xs sm:text-sm">
                    <i className="fas fa-download mr-2"></i> Export CSV
                  </Button>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 overflow-x-auto">
                  {universityData.by_university.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>University</TableHead>
                          <TableHead className="text-right">Students</TableHead>
                          <TableHead className="text-right">Payments</TableHead>
                          <TableHead className="text-right">Total Collected</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {universityData.by_university.map(row => (
                          <TableRow key={row.university ?? 'unspecified-university'} data-testid={`university-revenue-${encodeURIComponent(row.university ?? 'unspecified')}`}>
                            <TableCell className="font-medium">{row.university || 'Not specified'}</TableCell>
                            <TableCell className="text-right">{row.student_count}</TableCell>
                            <TableCell className="text-right">{row.payment_count}</TableCell>
                            <TableCell className="text-right font-bold">{formatCurrency(row.total_collected)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-8 text-gray-500">No university data for this period</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <i className="fas fa-university text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Click "Generate Report" to view university data</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
